import { StyleSheet, View } from 'react-native';

import { IconErrorFill, Skeleton, Text, XStack, tokens } from '@fhs/ui';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { isCreditCardExpired } from '@fhs-legacy/frontend/src/utils/payment';
import { PAYMENT_ENTRY_SAVED_CARD } from '@fhs-legacy/frontend/src/utils/test-ids';

import { ISavedCardInfo, PaymentMethodType } from '../../../payment/payment-types';
import { logPaymentMethodSelected } from '../../../utils/logging-utils';

import PaymentEntry from './action-payment-method-selection/payment-entry';
import { cardBrandIconMap } from './payment-method-icon-map';

export default function SavedCardEntry({
  loading,
  savedCardInfo,
  selectedPaymentMethod,
  currentSavedCardData,
  setSelectedSavedCard,
  setShowAddPaymentMethod,
  onClose,
}: {
  loading?: boolean;
  savedCardInfo?: ISavedCardInfo;
  selectedPaymentMethod: PaymentMethodType;
  currentSavedCardData: ISavedCardInfo | undefined;
  setSelectedSavedCard: (cardInfo: ISavedCardInfo) => void;
  setShowAddPaymentMethod: (show: boolean) => void;
  onClose(): void;
}) {
  const { logRBIEvent } = useCRMEventsContext();

  if (!savedCardInfo) {
    if (loading) {
      return (
        <PaymentEntry
          testID={PAYMENT_ENTRY_SAVED_CARD}
          onPress={() => {
            onClose();
          }}
          icon={<Skeleton style={{ height: 40, width: 40, borderRadius: 30 }} />}
          text="Getting saved info..."
          selected={false}
        />
      );
    }
    return null;
  }

  const card = savedCardInfo;
  const isExpired = isCreditCardExpired(card.expiryMonth, card.expiryYear);

  const isThisCardSelected =
    selectedPaymentMethod === 'savedCard' &&
    currentSavedCardData?.accountIdentifier === card.accountIdentifier;

  return (
    <PaymentEntry
      testID={PAYMENT_ENTRY_SAVED_CARD}
      onPress={() => {
        logPaymentMethodSelected(logRBIEvent, `Saved Card ${card.cardType}`);
        setSelectedSavedCard(card);
        setShowAddPaymentMethod(false);
        onClose();
      }}
      icon={cardBrandIconMap[card.cardType]}
      textReplacement={
        <View style={styles.container}>
          <View>
            <Text.Ui
              weight="semibold"
              size="lg"
            >{`${card.cardType} **** ${card.alias}`}</Text.Ui>
            <Text.Ui size="md">
              {`exp. ${card.expiryMonth}/${card.expiryYear}`}
            </Text.Ui>
            {isExpired && (
              <XStack style={styles.expiredLabel}>
                <IconErrorFill size={16} color={tokens.colors.$error} />
                <Text.Ui size="md" style={styles.expiredDate}>
                  Card Expired
                </Text.Ui>
              </XStack>
            )}
          </View>
        </View>
      }
      selected={isThisCardSelected}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    flexDirection: 'row',
  },
  expiredDate: {
    color: tokens.colors.$error,
  },
  expiredLabel: {
    alignItems: 'center',
    gap: 4,
  },
});
